<template>
  <div class="flex flex-col gap-4" data-testid="data-request-audience">
    <SelectButton :modelValue="draft.allStudents" :options="modes" optionLabel="label" optionValue="value"
      :allowEmpty="false" aria-label="Who to ask" data-testid="audience-mode"
      @update:modelValue="(all: boolean) => (draft.allStudents = all)" />

    <template v-if="!draft.allStudents">
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <label for="audience-levels" class="text-sm font-medium">{{ isTertiary ? 'Levels' : 'Classes' }}</label>
          <MultiSelect inputId="audience-levels" v-model="draft.classroomIds" :options="levels" optionLabel="label"
            optionValue="value" filter display="chip" :loading="loadingClasses" class="w-full"
            :placeholder="isTertiary ? 'Any level' : 'Whole classes'" data-testid="audience-levels" />
        </div>
        <div v-if="!isTertiary" class="flex flex-col gap-1.5">
          <label for="audience-arms" class="text-sm font-medium">Arms</label>
          <MultiSelect inputId="audience-arms" v-model="draft.sectionIds" :options="arms" optionLabel="label"
            optionValue="value" filter display="chip" :loading="loadingClasses" class="w-full" placeholder="Single arms"
            data-testid="audience-arms" />
        </div>
        <div v-if="isTertiary" class="flex flex-col gap-1.5">
          <label for="audience-programmes" class="text-sm font-medium">Programmes</label>
          <MultiSelect inputId="audience-programmes" v-model="draft.programmeIds" :options="programmeOptions"
            optionLabel="label" optionValue="value" filter display="chip" :loading="loadingSchoolProgramme" class="w-full"
            placeholder="Any programme" data-testid="audience-programmes" />
        </div>
      </div>
      <small v-if="isTertiary" class="text-surface-500">
        Choosing levels and programmes asks students in both, for example 100 Level Computer Science.
      </small>

      <div class="flex flex-col gap-1.5">
        <label for="audience-students" class="text-sm font-medium">Particular students</label>
        <AutoComplete inputId="audience-students" v-model="picked" :suggestions="filteredStudents" optionLabel="fullName"
          multiple fluid :minLength="2" placeholder="Search by name or matric number" data-testid="audience-students"
          @complete="searchStudents" @update:modelValue="onPick" />
        <small class="text-surface-500">Added on top of the classes chosen above.</small>
      </div>

      <div class="flex items-start gap-2.5">
        <Checkbox v-model="draft.includeLateJoiners" inputId="late-joiners" binary data-testid="audience-late-joiners" />
        <label for="late-joiners" class="cursor-pointer">
          <span class="text-sm font-medium">Also ask students who join these classes later</span>
          <span class="mt-0.5 block text-xs text-surface-500">They are added while the request is open.</span>
        </label>
      </div>
    </template>

    <Message v-if="error" severity="error" size="small" :closable="false" data-testid="audience-error">{{ error }}</Message>
    <p class="text-sm font-medium" data-testid="audience-count">
      <i v-if="counting" class="pi pi-spin pi-spinner mr-1 text-xs" />{{ countText }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/userStore';
import { useStudentSearch } from '@/composables/useStudentSearch';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useAudienceClassOptions } from '@/composables/queries/useAudienceClassOptions';
import { audienceCountText, type DataRequestDraft } from '@/utils/studentDataRequests';

/**
 * Who a data request goes to: all students, or classes and arms (levels and
 * programmes in a tertiary school) plus students picked by name, with a live
 * count of who that reaches.
 */
const props = defineProps<{
  count: number | null;
  counting: boolean;
  error?: string;
}>();

const draft = defineModel<DataRequestDraft>({ required: true });

const { isTertiary } = storeToRefs(useUserStore());
const { levels, arms, loading: loadingClasses } = useAudienceClassOptions({ tertiary: isTertiary });
const { programmes, loadingSchoolProgramme, loadSchoolProgramme } = useProgrammes({});
watch(isTertiary, (tertiary) => { if (tertiary) loadSchoolProgramme(); }, { immediate: true });
const programmeOptions = computed(() => programmes.value.map(p => ({ value: p.id, label: p.program_name })));

const modes = [
  { value: true, label: 'All students' },
  { value: false, label: 'Choose classes or students' },
];

const { filteredStudents, searchStudents } = useStudentSearch();
const picked = ref<Array<{ id: string; fullName?: string }>>(draft.value.students.map(s => ({ id: s.id, fullName: s.label })));
function onPick(value: Array<{ id: string; fullName?: string }>) {
  draft.value.students = (value ?? []).map(s => ({ id: s.id, label: s.fullName ?? s.id }));
}

const countText = computed(() => audienceCountText(props.count));
</script>
